import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { Product, ProductFormData } from '../types/product.types';
import { useProductCategories } from '../hooks/useProductCategories';
import { toast } from 'sonner';

interface ProductFormProps {
  product: Product | null;
  onSave: (data: ProductFormData) => void;
  onCancel: () => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({ product, onSave, onCancel }) => {
  const { categories, loading: loadingCategories } = useProductCategories();
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to get categoryId from product (no conversion needed now)
  const getCategoryId = (product: Product | undefined) => {
    return product?.categoryId || '';
  };

  // Use first category as default if available
  const defaultCategoryId = categories.length > 0 ? categories[0].id : '';

  const [formData, setFormData] = React.useState<ProductFormData>({
    name: product?.name || '',
    description: product?.description || '',
    categoryId: product ? getCategoryId(product) : defaultCategoryId,
    isAdditional: product?.isAdditional || false,
    isComplement: product?.isComplement || false,
  });

  // Update form data when categories are loaded (only on initial load)
  const categoriesLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (categories.length > 0 && !categoriesLoadedRef.current) {
      categoriesLoadedRef.current = true;

      setFormData(prev => {
        // Don't override if categoryId is already set
        if (prev.categoryId) return prev;

        if (product) {
          // For existing product, use categoryId directly
          return { ...prev, categoryId: getCategoryId(product) };
        } else {
          // For new product, use first category
          return { ...prev, categoryId: categories[0].id };
        }
      });
    }
  }, [categories, product]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que se haya seleccionado una categoría
    if (!formData.categoryId) {
      toast.error('Debes seleccionar una categoría');
      return;
    }

    onSave(formData);
  };

  const handleChange = (field: keyof ProductFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Nombre del Producto</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div>
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor="category">Categoría <span className="text-red-500">*</span></Label>
        <div className="relative" ref={categoryDropdownRef}>
          <button
            type="button"
            onClick={() => !loadingCategories && setShowCategoryDropdown(!showCategoryDropdown)}
            disabled={loadingCategories}
            className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={formData.categoryId ? 'text-gray-900' : 'text-gray-500'}>
              {loadingCategories
                ? 'Cargando...'
                : formData.categoryId
                  ? categories.find(c => c.id === formData.categoryId)?.name || 'Seleccionar categoría'
                  : 'Seleccionar categoría'
              }
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showCategoryDropdown && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg max-h-60 overflow-auto">
              {categories.map(category => (
                <div
                  key={category.id}
                  onClick={() => {
                    handleChange('categoryId', category.id);
                    setShowCategoryDropdown(false);
                  }}
                  className={`cursor-pointer px-3 py-2 text-sm hover:bg-rose-50 ${
                    formData.categoryId === category.id ? 'bg-rose-100 text-rose-700 font-medium' : 'text-gray-900'
                  }`}
                >
                  {category.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isAdditional"
            checked={formData.isAdditional}
            onCheckedChange={(checked) => handleChange('isAdditional', checked === true)}
          />
          <Label
            htmlFor="isAdditional"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Es Adicional
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="isComplement"
            checked={formData.isComplement}
            onCheckedChange={(checked) => handleChange('isComplement', checked === true)}
          />
          <Label
            htmlFor="isComplement"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Es Complemento
          </Label>
        </div>

      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!formData.categoryId || loadingCategories}>
          {loadingCategories ? 'Cargando...' : 'Guardar Producto'}
        </Button>
      </div>
    </form>
  );
};